import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	Typography,
	CircularProgress,
	Alert,
	Box,
} from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';

interface ConfirmationDialogProps {
	open: boolean;
	title: string;
	/** Optional icon rendered beside the title (e.g. Hyper Monitoring wireframe confirms). */
	titleIcon?: React.ReactNode;
	message?: string;
	/** Optional custom content (e.g. deposit list). When provided, renders instead of message. */
	content?: React.ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	/** When true, clicking outside or pressing ESC will not close the dialog. */
	disableBackdropClose?: boolean;
	onConfirm: () => void | Promise<void>;
	onCancel: () => void;
	confirmColor?: 'primary' | 'secondary' | 'error' | 'warning';
	loading?: boolean;
	error?: Error | null;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = React.memo(({
	open,
	title,
	titleIcon,
	message,
	content,
	confirmLabel,
	cancelLabel,
	disableBackdropClose = false,
	onConfirm,
	onCancel,
	confirmColor = 'error',
	loading = false,
	error = null,
}) => {
	const t = useAppSelector(selectTranslations);

	return (
		<Dialog
			open={open}
			onClose={disableBackdropClose ? undefined : onCancel}
			aria-labelledby="confirmation-dialog-title"
			aria-describedby="confirmation-dialog-description"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle id="confirmation-dialog-title">
				{titleIcon ? (
					<Box
						dir="rtl"
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: 1.75,
						}}
					>
						{titleIcon}
						<Typography
							variant="h6"
							component="h2"
							sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4, pt: 1 }}
						>
							{title}
						</Typography>
					</Box>
				) : (
					<Typography variant="h6" component="h2">
						{title}
					</Typography>
				)}
			</DialogTitle>
			<DialogContent>
				{content ? (
					content
				) : (
					<DialogContentText id="confirmation-dialog-description">
						{message}
					</DialogContentText>
				)}
				{error && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{error.message || 'An error occurred'}
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} color="inherit" variant="outlined" disabled={loading}>
					{cancelLabel || t('actions.cancel')}
				</Button>
				<Button
					onClick={onConfirm}
					color={confirmColor}
					variant="contained"
					autoFocus
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
				>
					{confirmLabel || t('actions.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
});

ConfirmationDialog.displayName = 'ConfirmationDialog';
